import { format, parse } from "date-fns";
import { GridFilterItem } from "@mui/x-data-grid-pro";
import { SharedSimulationKeys } from "@constants/index";

/**
 * Builds simulation filters from DataGrid filter items
 * Converts DataGrid filter format to API filter format, excluding direct filters
 *
 * @param filters - Array of GridFilterItem from DataGrid toolbar
 * @returns Formatted filter object for API or undefined if no valid filters
 *
 * @example
 * buildSimulationFilters([
 *   { field: 'name', operator: 'equals', value: 'test' }
 * ])
 * // Returns: { name: { eq: 'test' } }
 */
export const buildSimulationFilters = (filters?: Array<GridFilterItem>): any | undefined => {
    if (!filters || !filters.length) return undefined;
    const simulationFilters: any = {};
    const directFilters = [SharedSimulationKeys.IS_SHARED, SharedSimulationKeys.IS_SHARED_BY_ME];
    filters
        .filter((filter: GridFilterItem) => !directFilters.includes(filter.field as SharedSimulationKeys))
        .forEach((filter: GridFilterItem) => {
            if (filter.field && filter.value) {
                simulationFilters[filter.field] = {
                    [getOperatorFromDataGridToApi(filter.operator)]: filter.value,
                };
            }
        });
    return simulationFilters;
};

export const getOperatorFromDataGridToApi = (operator: string): string => {
    switch (operator) {
        case "equals":
            return "eq";
        case "startsWith":
            return "beginsWith";
        default:
            return operator;
    }
};

/**
 * truncateString function truncate the string and append elipsis.
 * @param str : input string
 * @param n : length, if string is greater then passed length then it will truncate
 */
export function truncateString(str: string, n: number): string {
    if (str == null) {
        return "";
    }
    if (str.length > n) {
        return str.substring(0, n) + "...";
    } else {
        return str;
    }
}

/**
 * Capitalizes the first letter of a string and lowercases the rest
 * @param str - String to capitalize
 * @returns Capitalized string (e.g., "HELLO" → "Hello", "world" → "World")
 */
export function capitalize(str = ""): string {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const validFilterOperations = ["equals", "startsWith"];

/**
 * formatFilterFromDataGrid connects the filter from the datagrid toolbar to the filters
 * used by the backend
 * @param Object : filterOptions object passed from datagrid toolbar with the status of
 * the datagrid toolbar filter
 * @param filterFields : list of strings that represent the fields available for filtering
 *  at the data that is displayed in the datagrid
 */
export const formatFilterFromDataGrid = (filterOptions: any, filterFields: Array<string>): object => {
    let filters: any | undefined;
    if (filterOptions.filterModel?.items?.length) {
        filters = {};
        filterOptions.filterModel.items.forEach((filterItem: any) => {
            if (filterFields.includes(filterItem.field) && validFilterOperations.includes(filterItem.operator)) {
                filters[filterItem.field] = {
                    [getOperatorFromDataGridToApi(filterItem.operator)]: filterItem.value,
                };
            }
        });
    }
    return filters;
};

/**
 * Creates a deep copy of the input value, handling various data types
 * including objects, arrays, dates, maps, and sets.
 *
 * @template T - The type of the input value
 * @param input - The value to create a deep copy of
 * @returns A deep copy of the input value
 *
 * @example
 * const obj = {
 *   date: new Date(),
 *   arr: [1, 2, {x: 3}],
 *   map: new Map([['a', 1]]),
 *   set: new Set([1, 2, 3])
 * };
 * const copy = createDeepCopy(obj);
 */
export function createDeepCopy<T>(input: T): T {
    // Handle null and non-object types
    if (input === null || typeof input !== "object") {
        return input;
    }

    // Handle arrays
    if (Array.isArray(input)) {
        return input.map(createDeepCopy) as unknown as T;
    }

    // Handle Date objects
    if (input instanceof Date) {
        return new Date(input.getTime()) as unknown as T;
    }

    // Handle Map objects
    if (input instanceof Map) {
        const copiedMap = new Map();
        input.forEach((value, key) => {
            copiedMap.set(createDeepCopy(key), createDeepCopy(value));
        });
        return copiedMap as unknown as T;
    }

    // Handle Set objects
    if (input instanceof Set) {
        const copiedSet = new Set();
        input.forEach((value) => {
            copiedSet.add(createDeepCopy(value));
        });
        return copiedSet as unknown as T;
    }

    // Handle plain objects
    const copiedObj: Record<string | symbol, any> = {};
    for (const key of Reflect.ownKeys(input)) {
        copiedObj[key as string | symbol] = createDeepCopy((input as any)[key]);
    }

    return copiedObj as T;
}

/**
 * Generates a timestamped filename with optional username prefix
 * @param prefix - The file prefix (e.g., 'custom_drivers')
 * @param simulationId - The simulation ID
 * @param username - Optional username prefix
 * @param extension - Optional file extension (without dot)
 * @returns A formatted filename with timestamp
 *
 * @example
 * generateTimestampedFileName('custom_drivers', 'sim123')
 * // Returns: 'custom_drivers_sim123_2023-12-25T10-30-45-123Z'
 *
 * generateTimestampedFileName('custom_drivers', 'sim123', 'john')
 * // Returns: 'john_custom_drivers_sim123_2023-12-25T10-30-45-123Z'
 *
 * generateTimestampedFileName('custom_drivers', 'sim123', undefined, 'xlsx')
 * // Returns: 'custom_drivers_sim123_2023-12-25T10-30-45-123Z.xlsx'
 *
 * generateTimestampedFileName('custom_drivers', 'sim123', 'john', 'xlsx')
 * // Returns: 'john_custom_drivers_sim123_2023-12-25T10-30-45-123Z.xlsx'
 */
export function generateTimestampedFileName(prefix: string, simulationId: string, username?: string, extension?: string): string {
    const timestamp = new Date().toISOString().replace(/:/g, "-").replace(/\./g, "-");
    const fileName = username ? `${username}_${prefix}_${simulationId}_${timestamp}` : `${prefix}_${simulationId}_${timestamp}`;
    return extension ? `${fileName}.${extension}` : fileName;
}

/**
 * Calculates selected time periods based on start and end period strings
 * @param startPeriod - Start period string (format: YYYYMM like "202301")
 * @param endPeriod - End period string (format: YYYYMM like "202312")
 * @returns Array of year strings representing the time periods
 *
 * @example
 * calculateSelectedTimePeriods("202301", "202312")
 * // Returns: ["2023"]
 *
 * calculateSelectedTimePeriods("202301", "202401")
 * // Returns: ["2023", "2024"]
 *
 * calculateSelectedTimePeriods("", "")
 * // Returns: []
 */
export function calculateSelectedTimePeriods(startPeriod: string, endPeriod: string): Array<string> {
    if (!startPeriod || !endPeriod) {
        return [];
    }

    // Extract year from period string (remove last 2 characters which are month)
    const startNum = parseInt(startPeriod.substring(0, startPeriod.length - 2));
    const endNum = parseInt(endPeriod.substring(0, endPeriod.length - 2));

    const periods: Array<string> = [];
    for (let i = startNum; i <= endNum; i++) {
        periods.push(i.toString());
    }

    return periods;
}

/**
 * Formats a period string to month year format (e.g., "202509" -> "Sep 2025")
 *
 * @param period - Period string in YYYYMM format
 * @returns Formatted month string
 */
export const formatPeriodToMonthYear = (period: string | number): string => {
    const periodStr = String(period);
    const date = parse(periodStr, "yyyyMM", new Date());
    return format(date, "MMM yyyy");
};
